import { styled } from "@pigment-css/react";
import { Switch as RadixSwitch } from "radix-ui";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

const TRACK_WIDTH = 64;
const KNOB_WIDTH = 39;
const KNOB_HEIGHT = 24;
const KNOB_INSET = 2;

const Root = styled(RadixSwitch.Root)(({ theme }) => ({
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  width: TRACK_WIDTH,
  height: KNOB_HEIGHT + KNOB_INSET * 2,
  padding: 0,
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: "rgba(60, 60, 67, 0.3)",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  ...pressInteraction(theme),
  // On state reuses the selected-day fill so the toggle stays in the calendar's monochrome family.
  '&[data-state="checked"]': {
    background: theme.fieldSalesTwo.colors.neutral[900],
  },
  "&:disabled": {
    cursor: "default",
  },
}));

const Thumb = styled(RadixSwitch.Thumb)(({ theme }) => ({
  display: "block",
  width: KNOB_WIDTH,
  height: KNOB_HEIGHT,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: "#fff",
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  transform: `translateX(${KNOB_INSET}px)`,
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  willChange: "transform",
  '&[data-state="checked"]': {
    transform: `translateX(${TRACK_WIDTH - KNOB_WIDTH - KNOB_INSET}px)`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  "aria-label": string;
  disabled?: boolean;
}

export function Switch({ checked, onCheckedChange, "aria-label": ariaLabel, disabled }: SwitchProps) {
  return (
    <Root checked={checked} onCheckedChange={onCheckedChange} aria-label={ariaLabel} disabled={disabled}>
      <Thumb />
    </Root>
  );
}
